/**
 * RealtimeContext
 * ---------------
 * Oturum açılınca `/api/ws` WebSocket'ine bağlanır. Sunucudan gelen
 * "invalidate" mesajları için `window` üzerinde özel event tetiklenir:
 *
 *   event name: `rt:{resource}`  (örn. rt:cutting_task)
 *   detail:     { action, entity_id }
 *
 * Sayfalar `useRealtimeInvalidate([resources], cb)` hook'u ile dinler
 * ve ilgili verileri yeniden çeker. Bağlantı koparsa expo-backoff ile
 * yeniden bağlanır. 30s heartbeat → ara proxy keep-alive.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext({ connected: false });

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const unmountedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const pingRef = useRef(null);

  const connect = useCallback(() => {
    if (!token || unmountedRef.current) return;
    // Zaten açık/bağlanıyorsa yeniden deneme
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws?token=" + encodeURIComponent(token);
    try {
      const sock = new WebSocket(wsUrl);
      wsRef.current = sock;
      sock.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        // Bağlantı (yeniden) kurulunca kayıp olayları telafi için
        // tüm kaynaklara "generic refresh" sinyali yolla.
        window.dispatchEvent(new CustomEvent("rt:reconnect"));
        ["cutting_task", "pending_order", "retail_order", "factory_order",
          "roll", "reservation", "shipping", "store_delivery", "customer"].forEach((r) => {
          window.dispatchEvent(new CustomEvent(`rt:${r}`, { detail: { action: "reconnect" } }));
        });
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { sock.send("ping"); } catch {}
        }, 30000);
      };
      sock.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "invalidate" && msg.resource) {
            window.dispatchEvent(
              new CustomEvent(`rt:${msg.resource}`, { detail: msg.data || {} })
            );
          }
        } catch {
          // "pong" / düz string — yoksay
        }
      };
      const schedule = () => {
        setConnected(false);
        if (pingRef.current) clearInterval(pingRef.current);
        if (unmountedRef.current || !token) return;
        const delay = Math.min(10000, 500 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      };
      sock.onclose = schedule;
      sock.onerror = () => { try { sock.close(); } catch {} };
    } catch (e) {
      // Non-blocking
    }
  }, [token]);

  useEffect(() => {
    unmountedRef.current = false;
    if (user && token) connect();
    // Sekme tekrar görünür olunca veya pencere odak alınca zorla yeniden bağlan
    const onVisible = () => {
      if (document.visibilityState === "visible" && user && token) connect();
    };
    const onFocus = () => { if (user && token) connect(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);
    return () => {
      unmountedRef.current = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
      if (pingRef.current) clearInterval(pingRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user, token, connect]);

  return (
    <RealtimeContext.Provider value={{ connected }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

/**
 * Belirli kaynaklar değiştiğinde callback çalıştırır.
 *   useRealtimeInvalidate(["cutting_task", "pending_order"], reload);
 *
 * callback birden fazla kez çağrılabilir (her kaynak için). Küçük bir
 * throttle (150ms) ile ard arda sinyallerde tek seferde yenileme sağlanır.
 */
export function useRealtimeInvalidate(resources, callback) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const list = Array.isArray(resources) ? resources : [resources];
  const key = list.join("|");

  useEffect(() => {
    let timer = null;
    const trigger = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        try { cbRef.current?.(); } catch {}
      }, 150);
    };
    const handlers = list.map((res) => {
      const evt = `rt:${res}`;
      window.addEventListener(evt, trigger);
      return [evt, trigger];
    });
    return () => {
      if (timer) clearTimeout(timer);
      handlers.forEach(([evt, h]) => window.removeEventListener(evt, h));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
